import { lazy, Suspense } from "react";
import { Switch, Route, Router as WouterRouter } from "wouter";
import { QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { queryClient } from "@/lib/queryClient";
import { AppShell } from "@/components/layout/app-shell";
import { MaintenanceGate } from "@/components/maintenance-gate";
import LandingPage from "@/pages/landing";
import LoginPage from "@/pages/login";
import VerifyPage from "@/pages/verify";
import VerifyUeiPage from "@/pages/verify-uei";

// Code-split the heavy authenticated routes. Each chunk is fetched only when
// the user navigates to that page, dropping the initial JS payload to the
// login/verify shell. Suspense renders a small placeholder while a chunk is
// in flight.
const DashboardPage = lazy(() => import("@/pages/dashboard"));
const VendorProfilePage = lazy(() => import("@/pages/vendor-profile"));
const AgencyExperiencePage = lazy(() => import("@/pages/agency-experience"));
const DocumentsPage = lazy(() => import("@/pages/documents"));
const SearchPage = lazy(() => import("@/pages/search"));
const VendorDetailPage = lazy(() => import("@/pages/vendor-detail"));
const DiscoverPage = lazy(() => import("@/pages/discover"));
const DiscoverDetailPage = lazy(() => import("@/pages/discover-detail"));
const WatchlistPage = lazy(() => import("@/pages/watchlist"));
const TeamingPage = lazy(() => import("@/pages/teaming"));
const MessagesPage = lazy(() => import("@/pages/messages"));
const AdminPage = lazy(() => import("@/pages/admin"));
const ConciergeAdminPage = lazy(() => import("@/pages/concierge-admin"));
const ConciergeReviewPage = lazy(() => import("@/pages/concierge-review"));
const MarketplacePage = lazy(() => import("@/pages/marketplace"));
const MarketplaceOpportunityDetail = lazy(
  () => import("@/pages/marketplace-opportunity-detail"),
);
const MarketplaceRoadmapDetail = lazy(() => import("@/pages/marketplace-roadmap-detail"));
const MarketplaceForumThreadPage = lazy(() => import("@/pages/marketplace-forum-thread"));
const NotificationsSettingsPage = lazy(() => import("@/pages/notifications-settings"));
const EngagementAdminPage = lazy(() => import("@/pages/engagement-admin"));
const TenantSettingsPage = lazy(() => import("@/pages/tenant-settings"));
const OpportunitiesPage = lazy(() => import("@/pages/opportunities"));
const OpportunityDetailPage = lazy(() => import("@/pages/opportunity-detail"));
const AnalyzeOpportunityPage = lazy(() => import("@/pages/analyze-opportunity"));
const PartnersPage = lazy(() => import("@/pages/partners"));
const SubmissionsPage = lazy(() => import("@/pages/submissions"));
const TeamWorkspacesPage = lazy(() => import("@/pages/team-workspaces"));
const InviteAcceptPage = lazy(() => import("@/pages/invite-accept"));
const NotFound = lazy(() => import("@/pages/not-found"));

function RouteFallback() {
  return (
    <div className="flex items-center justify-center py-16 text-sm text-muted-foreground">
      Loading…
    </div>
  );
}

function Shelled({ children }: { children: React.ReactNode }) {
  return (
    <AppShell>
      <Suspense fallback={<RouteFallback />}>{children}</Suspense>
    </AppShell>
  );
}

function Router() {
  return (
    <Switch>
      <Route path="/" component={LandingPage} />
      <Route path="/login" component={LoginPage} />
      <Route path="/verify" component={VerifyPage} />
      <Route path="/verify-uei" component={VerifyUeiPage} />
      <Route path="/invites/accept">
        <Suspense fallback={<RouteFallback />}>
          <InviteAcceptPage />
        </Suspense>
      </Route>
      <Route path="/settings/team">
        <Shelled>
          <TenantSettingsPage />
        </Shelled>
      </Route>
      <Route path="/dashboard">
        <Shelled>
          <DashboardPage />
        </Shelled>
      </Route>
      <Route path="/vendor-profile">
        <Shelled>
          <VendorProfilePage />
        </Shelled>
      </Route>
      <Route path="/agency-experience">
        <Shelled>
          <AgencyExperiencePage />
        </Shelled>
      </Route>
      <Route path="/documents">
        <Shelled>
          <DocumentsPage />
        </Shelled>
      </Route>
      <Route path="/search">
        <Shelled>
          <SearchPage />
        </Shelled>
      </Route>
      <Route path="/vendor/:id">
        <Shelled>
          <VendorDetailPage />
        </Shelled>
      </Route>
      <Route path="/discover">
        <Shelled>
          <DiscoverPage />
        </Shelled>
      </Route>
      <Route path="/discover/:uei">
        <Shelled>
          <DiscoverDetailPage />
        </Shelled>
      </Route>
      <Route path="/watchlist">
        <Shelled>
          <WatchlistPage />
        </Shelled>
      </Route>
      <Route path="/opportunities">
        <Shelled>
          <OpportunitiesPage />
        </Shelled>
      </Route>
      <Route path="/opportunities/analyze">
        <Shelled>
          <AnalyzeOpportunityPage />
        </Shelled>
      </Route>
      <Route path="/opportunities/:id">
        <Shelled>
          <OpportunityDetailPage />
        </Shelled>
      </Route>
      <Route path="/partners">
        <Shelled>
          <PartnersPage />
        </Shelled>
      </Route>
      <Route path="/submissions">
        <Shelled>
          <SubmissionsPage />
        </Shelled>
      </Route>
      <Route path="/teaming">
        <Shelled>
          <TeamingPage />
        </Shelled>
      </Route>
      <Route path="/team-workspaces">
        <Shelled><TeamWorkspacesPage /></Shelled>
      </Route>
      <Route path="/team-workspaces/:id">
        <Shelled><TeamWorkspacesPage /></Shelled>
      </Route>
      <Route path="/messages">
        <Shelled>
          <MessagesPage />
        </Shelled>
      </Route>
      <Route path="/admin">
        <Shelled>
          <AdminPage />
        </Shelled>
      </Route>
      <Route path="/admin/concierge">
        <Shelled>
          <ConciergeAdminPage />
        </Shelled>
      </Route>
      <Route path="/vendor-review">
        <Shelled>
          <ConciergeReviewPage />
        </Shelled>
      </Route>
      <Route path="/admin/engagement">
        <Shelled>
          <EngagementAdminPage />
        </Shelled>
      </Route>
      <Route path="/marketplace">
        <Shelled>
          <MarketplacePage />
        </Shelled>
      </Route>
      <Route path="/marketplace/opportunities/:id">
        <Shelled>
          <MarketplaceOpportunityDetail />
        </Shelled>
      </Route>
      <Route path="/marketplace/roadmap/:id">
        <Shelled>
          <MarketplaceRoadmapDetail />
        </Shelled>
      </Route>
      <Route path="/marketplace/forum/:id">
        <Shelled>
          <MarketplaceForumThreadPage />
        </Shelled>
      </Route>
      <Route path="/settings/notifications">
        <Shelled>
          <NotificationsSettingsPage />
        </Shelled>
      </Route>
      <Route>
        <Suspense fallback={<RouteFallback />}>
          <NotFound />
        </Suspense>
      </Route>
    </Switch>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <MaintenanceGate>
          <WouterRouter base={import.meta.env.BASE_URL.replace(/\/$/, "")}>
            <Router />
          </WouterRouter>
        </MaintenanceGate>
        <Toaster richColors position="top-right" />
      </TooltipProvider>
    </QueryClientProvider>
  );
}

export default App;
